import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus, RefreshCw, Star } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  useCreateTimesheetType,
  useDeleteTimesheetType,
  useTimesheetTypes,
  useUpdateTimesheetType,
  type TimesheetType,
} from './timesheet-types.api';

/**
 * The kinds of shift a timesheet entry can be.
 *
 * Small enough that the whole list fits on the page: no search, no paging.
 * Both names are shown side by side rather than one per the reader's language,
 * because the person maintaining the list is the one who has to keep the pair
 * in step.
 */
export function TimesheetTypesPage() {
  const { t } = useTranslation();
  const types = useTimesheetTypes();

  const [editing, setEditing] = useState<TimesheetType | 'new' | null>(null);

  const rows = types.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('timesheetTypes.title')}
        description={t('timesheetTypes.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={types.isFetching}
              onClick={() => void types.refetch()}
            >
              <RefreshCw className={types.isFetching ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">
                {t('timesheetTypes.refresh')}
              </span>
            </Button>
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus />
              <span className="hidden sm:inline">
                {t('timesheetTypes.newType')}
              </span>
            </Button>
          </>
        }
      />

      {types.isPending && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-14 rounded-xl" />
          ))}
        </div>
      )}

      {types.isError && (
        <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
          {t('timesheetTypes.loadFailed')}
        </p>
      )}

      {types.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={types.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
            {t('timesheetTypes.noTypes')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={types.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              types.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setEditing(row)}
                className="hover:bg-accent flex w-full items-center gap-3 p-3 text-left transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{row.nameEn}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {row.nameEs}
                  </p>
                </div>
                {row.isDefault && (
                  <Badge variant="success" className="shrink-0">
                    <Star className="size-3" />
                    {t('timesheetTypes.default')}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <TypeEditorSheet
        type={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}

const BLANK = { nameEn: '', nameEs: '', isDefault: false };

function TypeEditorSheet({
  type,
  open,
  onOpenChange,
}: {
  type: TimesheetType | 'new' | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const create = useCreateTimesheetType();
  const update = useUpdateTimesheetType();
  const remove = useDeleteTimesheetType();

  const existing = type !== 'new' ? type : null;
  const [draft, setDraft] = useState(BLANK);
  const [hydrated, setHydrated] = useState<string | null>(null);

  const key = existing?.id ?? (type === 'new' ? 'new' : null);
  if (type && hydrated !== key) {
    setHydrated(key);
    setDraft(
      existing
        ? {
            nameEn: existing.nameEn ?? '',
            nameEs: existing.nameEs ?? '',
            isDefault: existing.isDefault,
          }
        : BLANK
    );
  }

  if (!type) return null;

  const busy = create.isPending || update.isPending || remove.isPending;
  // Both names are required: the list is read in either language, and a blank
  // one shows up as an empty row rather than falling back.
  const complete = draft.nameEn.trim() !== '' && draft.nameEs.trim() !== '';
  const failed = () => toast.error(t('timesheetTypes.actionFailed'));

  function save() {
    const input = {
      nameEn: draft.nameEn.trim(),
      nameEs: draft.nameEs.trim(),
      isDefault: draft.isDefault,
    };
    const done = {
      onSuccess: () => {
        toast.success(t('timesheetTypes.saved'));
        onOpenChange(false);
      },
      onError: failed,
    };

    if (existing) update.mutate({ id: existing.id, input }, done);
    else create.mutate(input, done);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {existing
              ? t('timesheetTypes.editType')
              : t('timesheetTypes.newType')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <div className="space-y-1.5">
            <Label>{t('timesheetTypes.nameEn')}</Label>
            <Input
              value={draft.nameEn}
              disabled={busy}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  nameEn: event.target.value,
                }))
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('timesheetTypes.nameEs')}</Label>
            <Input
              value={draft.nameEs}
              disabled={busy}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  nameEs: event.target.value,
                }))
              }
            />
          </div>

          <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
            <span className="min-w-0">
              <span className="block text-sm">
                {t('timesheetTypes.default')}
              </span>
              <span className="text-muted-foreground block text-xs">
                {t('timesheetTypes.defaultHint')}
              </span>
            </span>
            <Switch
              checked={draft.isDefault}
              disabled={busy}
              onCheckedChange={(isDefault) =>
                setDraft((current) => ({ ...current, isDefault }))
              }
            />
          </label>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {existing ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={busy}>
                    {remove.isPending && <Loader2 className="animate-spin" />}
                    {t('timesheetTypes.delete')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('timesheetTypes.delete')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('timesheetTypes.deletePrompt', {
                        name: existing.nameEn ?? existing.nameEs ?? '',
                      })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() =>
                        remove.mutate(existing.id, {
                          onSuccess: () => {
                            toast.success(t('timesheetTypes.deleted'));
                            onOpenChange(false);
                          },
                          onError: failed,
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <span />
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button disabled={busy || !complete} onClick={save}>
                {(create.isPending || update.isPending) && (
                  <Loader2 className="animate-spin" />
                )}
                {existing ? t('common.save') : t('timesheetTypes.create')}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
